import uniqueId from "lodash/uniqueId";

import type { Competitor, Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import {
    meetingGoalOptions,
    MEETING_GOAL_OTHER_OPTION,
} from "app/pages/sales-intelligence/pages/ai-agent/components/MeetingGoal/MeetingGoal";
import {
    createNewContact,
    parseContactString,
    stringifyContact,
} from "app/pages/sales-intelligence/pages/ai-agent/utils/contactUtils";

export const CONTACTS_SEPARATOR = ";";

export const mapMeetingGoalToFormValue = (meetingGoal: string): string => {
    if (!meetingGoalOptions.includes(meetingGoal)) {
        return MEETING_GOAL_OTHER_OPTION;
    }

    return meetingGoal;
};

export const mapSelectedContactsToFormValue = (contacts: Contact[]): string => {
    return contacts.map((contact) => stringifyContact(contact)).join(CONTACTS_SEPARATOR);
};

export const mapFormValueToSelectedContacts = (value: string): Contact[] => {
    if (!value) {
        return [];
    }
    return value.split(CONTACTS_SEPARATOR).map((contactString) => createNewContact(parseContactString(contactString)));
};

export const isContactMatchedByAttendee = (contact: Contact, attendeeIdentifier: string): boolean => {
    const lowerIdentifier = attendeeIdentifier.toLowerCase();
    return (
        (!!contact.name && contact.name.toLowerCase().includes(lowerIdentifier)) ||
        (!!contact.email && contact.email.toLowerCase() === lowerIdentifier)
    );
};

export const processContactsWithSelection = (
    rawContacts: Contact[],
    attendeeNames: string[],
    existingContacts: Contact[],
): Contact[] => {
    const existingContactsMap = new Map(
        existingContacts.map((contact) => {
            const key = contact.email || contact.name || "";
            return [key, contact];
        }),
    );

    return rawContacts.map((contact) => {
        const isSelected = attendeeNames.some((name) => isContactMatchedByAttendee(contact, name));
        const contactKey = contact.email || contact.name || "";
        const existingContact = existingContactsMap.get(contactKey);
        return {
            ...contact,
            isSelected,
            id: existingContact?.id || uniqueId(),
            isSuggested: true,
        };
    });
};

export const hasCompetitorsChanged = (prevCompetitors: Competitor[], newCompetitors: Competitor[]): boolean => {
    if (prevCompetitors.length !== newCompetitors.length) {
        return true;
    }

    return prevCompetitors.some(
        (prev, index) =>
            !newCompetitors[index] ||
            prev.domain !== newCompetitors[index].domain ||
            prev.companyName !== newCompetitors[index].companyName,
    );
};

export const getContactsKey = (contacts: Contact[]): string => {
    const normalized = contacts.map((c) => ({ name: c.name || "", email: c.email || "" }));
    normalized.sort((a, b) => {
        const emailCompare = a.email.localeCompare(b.email);
        if (emailCompare !== 0) {
            return emailCompare;
        }
        return a.name.localeCompare(b.name);
    });
    return JSON.stringify(normalized);
};

export const parseAttendeeNames = (attendee?: string): string[] => {
    if (!attendee) {
        return [];
    }
    const decodedAttendee = decodeURIComponent(attendee);
    return decodedAttendee.split(",").map((name) => name.trim());
};
